import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Tooltip,
  Stack,
  Avatar,
  Menu,
  MenuItem,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  InputAdornment,
} from '@mui/material';
import {
  Delete,
  Edit,
  Add,
  Visibility,
  Search,
  MoreVert,
  ViewModule,
  ViewList,
  TextFields,
  Archive,
  ContentCopy,
  History,
  Download,
  StarBorder,
  Close,
  CheckCircle,
  RestartAlt,
  Clear,
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { api } from '../context/AuthContext';
import API_BASE_URL from '../Config/config';

const PLACEHOLDERS = [
  { label: 'Candidate Name', value: '{candidate_name}' },
  { label: 'Job Title', value: '{job_title}' },
  { label: 'Company Name', value: '{company_name}' },
  { label: 'Salary', value: '{salary}' },
  { label: 'Joining Date', value: '{joining_date}' },
];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ size: ['small', false, 'large', 'huge'] }],
    [{ align: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'clean'],
  ],
};

const QUILL_FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'size',
  'align',
  'list',
  'bullet',
  'blockquote',
];

const isHtmlContent = (str) => /<[a-z][\s\S]*>/i.test(str || '');

const stripHtml = (html) =>
  (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const CREATED_OPTIONS = [
  'Created Today',
  'Last 7 Days',
  'Last 30 Days',
  'Oldest First',
  'Newest First',
];

const SORT_OPTIONS = [
  'Template Name A-Z',
  'Template Name Z-A',
  'Recently Updated',
  'Most Used',
  'Least Used',
];

const PAGE_SIZES = [10, 20, 50, 100];

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
};

const getCreatedAt = (template) =>
  template?.created_at || template?.createdAt || template?.created_on || null;

const getUpdatedAt = (template) =>
  template?.updated_at || template?.updatedAt || template?.modified_at || null;

const getStatus = (template) => {
  if (template?.archived || template?.is_archived) return 'Archived';
  if (template?.is_default || template?.default_template) return 'Default';
  if (template?.is_draft || template?.status === 'draft') return 'Draft';
  if (template?.status === 'active') return 'Active';
  return 'Custom';
};

const getStatusColor = (status) => {
  switch (status) {
    case 'Default':
      return 'primary';
    case 'Draft':
      return 'warning';
    case 'Archived':
      return 'default';
    case 'Active':
      return 'success';
    case 'Custom':
      return '#1E3A8A';
    default:
      return 'info';
  }
};

const getChipProps = (status) => {
  const color = getStatusColor(status);
  if (color === '#1E3A8A') {
    return { sx: { bgcolor: 'rgba(30, 58, 138, 0.1)', color: '#1E3A8A', fontWeight: 600 } };
  }
  return { color, sx: { fontWeight: 600 } };
};

const OfferLetterTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({ name: '', content: '' });
  const [letterheadImageUrl, setLetterheadImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [letterheadPreview, setLetterheadPreview] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [createdFilter, setCreatedFilter] = useState('');
  const [sortBy, setSortBy] = useState('Recently Updated');
  const [viewMode, setViewMode] = useState('grid');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [actionsTemplate, setActionsTemplate] = useState(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState({ content: '', letterheadUrl: '' });

  const [aiLoading, setAiLoading] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiData, setAiData] = useState({
    candidate_name: '{candidate_name}',
    job_title: '{job_title}',
    company_name: '{company_name}',
    salary: '{salary}',
    joining_date: '{joining_date}',
    additional_info: ''
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const quillRef = useRef(null);

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/offer-letters/templates');
      setTemplates(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      showSnackbar('Failed to fetch templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, createdFilter, sortBy, viewMode, pageSize]);

  const handleOpenDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({ name: template.name, content: template.content });
      setLetterheadImageUrl(template.letterhead_url || '');
      setLetterheadPreview(template.letterhead_url || null);
    } else {
      setEditingTemplate(null);
      setFormData({ name: '', content: '' });
      setLetterheadImageUrl('');
      setLetterheadPreview(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTemplate(null);
    setFormData({ name: '', content: '' });
    setLetterheadImageUrl('');
    setLetterheadPreview(null);
  };

  const handleNameChange = (e) =>
    setFormData((prev) => ({ ...prev, name: e.target.value }));

  const handleContentChange = (value) =>
    setFormData((prev) => ({ ...prev, content: value }));

  const insertPlaceholder = (placeholder) => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection(true);
      const idx = range ? range.index : editor.getLength() - 1;
      editor.insertText(idx, placeholder, 'user');
      editor.setSelection(idx + placeholder.length);
    } else {
      setFormData((prev) => ({ ...prev, content: prev.content + placeholder }));
    }
  };

  const handleGenerateAI = async () => {
    setAiLoading(true);
    try {
      const response = await api.post('/offer-letters/generate-ai', aiData);
      setFormData((prev) => ({ ...prev, content: response.data.content }));
      setAiDialogOpen(false);
      showSnackbar('Offer letter generated successfully!');
    } catch (error) {
      console.error('AI Generation Error:', error);
      showSnackbar('Failed to generate with AI', 'error');
    } finally {
      setAiLoading(false);
    }
  };

  const handleImproveAI = async (action = 'improve') => {
    if (!formData.content) return;
    setAiLoading(true);
    try {
      const response = await api.post('/offer-letters/improve-ai', {
        content: formData.content,
        action,
      });
      setFormData((prev) => ({ ...prev, content: response.data.content }));
      showSnackbar(`Offer letter ${action === 'improve' ? 'improved' : 'regenerated'} successfully!`);
    } catch (error) {
      console.error('AI Improvement Error:', error);
      showSnackbar(`Failed to ${action} with AI`, 'error');
    } finally {
      setAiLoading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      showSnackbar('File size should be less than 2MB', 'error');
      return;
    }

    setUploading(true);
    const data = new FormData();
    data.append('letterheadImage', file);

    try {
      const response = await api.post('/offer-letters/upload-letterhead', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLetterheadImageUrl(response.data.url);
      setLetterheadPreview(response.data.url);
      showSnackbar('Letterhead uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      showSnackbar('Failed to upload letterhead', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const templateData = {
        name: formData.name,
        content: formData.content,
        letterhead_url: letterheadImageUrl,
      };

      if (editingTemplate) {
        await api.patch(`/offer-letters/templates/${editingTemplate.id}`, templateData);
        showSnackbar('Template updated successfully');
      } else {
        await api.post('/offer-letters/templates', templateData);
        showSnackbar('Template created successfully');
      }

      fetchTemplates();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving template:', error.response?.data || error.message);
      showSnackbar(error.response?.data?.message || 'Failed to save template', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await api.delete(`/offer-letters/templates/${id}`);
        showSnackbar('Template deleted successfully');
        fetchTemplates();
      } catch (error) {
        console.error('Error deleting template:', error);
        showSnackbar('Failed to delete template', 'error');
      }
    }
  };

  const handlePreview = (template) => {
    setPreviewData({
      content: template.content,
      letterheadUrl: template.letterhead_url,
    });
    setPreviewOpen(true);
  };

  const handleDuplicate = async (template) => {
    try {
      const payload = {
        name: `${template.name} Copy`,
        content: template.content,
        letterhead_url: template.letterhead_url || '',
      };
      await api.post('/offer-letters/templates', payload);
      showSnackbar('Template duplicated successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Error duplicating template:', error);
      showSnackbar('Failed to duplicate template', 'error');
    }
  };

  const handleArchive = async (template) => {
    try {
      await api.patch(`/offer-letters/templates/${template.id}`, {
        name: template.name,
        content: template.content,
        letterhead_url: template.letterhead_url || '',
        archived: true,
      });
      showSnackbar('Template archived successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Error archiving template:', error);
      showSnackbar('Failed to archive template', 'error');
    }
  };

  const handleSetDefault = async (template) => {
    try {
      await api.patch(`/offer-letters/templates/${template.id}`, {
        name: template.name,
        content: template.content,
        letterhead_url: template.letterhead_url || '',
        is_default: true,
      });
      showSnackbar('Template set as default');
      fetchTemplates();
    } catch (error) {
      console.error('Error setting default template:', error);
      showSnackbar('Failed to set default template', 'error');
    }
  };

  const handleExport = (template) => {
    const payload = {
      id: template.id,
      name: template.name,
      content: template.content,
      letterhead_url: template.letterhead_url || '',
      created_at: getCreatedAt(template),
      updated_at: getUpdatedAt(template),
      status: getStatus(template),
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json',
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template.name || 'template'}.json`;
    a.click();
    URL.revokeObjectURL(url);

    showSnackbar('Template exported successfully');
  };

  const openActionsMenu = (event, template) => {
    setActionsAnchorEl(event.currentTarget);
    setActionsTemplate(template);
  };

  const closeActionsMenu = () => {
    setActionsAnchorEl(null);
    setActionsTemplate(null);
  };

  const filteredTemplates = useMemo(() => {
    let list = [...templates];

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter((template) => {
        const text = `${template.name || ''} ${stripHtml(template.content)}`.toLowerCase();
        return text.includes(q);
      });
    }

    if (createdFilter) {
      const now = new Date();
      list = list.filter((template) => {
        const createdAt = getCreatedAt(template);
        if (!createdAt) return createdFilter === 'Oldest First';

        const createdDate = new Date(createdAt);
        if (Number.isNaN(createdDate.getTime())) return false;

        const diffDays = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));

        switch (createdFilter) {
          case 'Created Today':
            return diffDays <= 0;
          case 'Last 7 Days':
            return diffDays <= 7;
          case 'Last 30 Days':
            return diffDays <= 30;
          case 'Oldest First':
          case 'Newest First':
            return true;
          default:
            return true;
        }
      });
    }

    list.sort((a, b) => {
      const aName = (a.name || '').toLowerCase();
      const bName = (b.name || '').toLowerCase();
      const aUpdated = getUpdatedAt(a) ? new Date(getUpdatedAt(a)).getTime() : 0;
      const bUpdated = getUpdatedAt(b) ? new Date(getUpdatedAt(b)).getTime() : 0;

      switch (sortBy) {
        case 'Template Name A-Z':
          return aName.localeCompare(bName);
        case 'Template Name Z-A':
          return bName.localeCompare(aName);
        case 'Recently Updated':
          return bUpdated - aUpdated;
        case 'Most Used':
          return 0;
        case 'Least Used':
          return 0;
        default:
          return bUpdated - aUpdated;
      }
    });

    if (createdFilter === 'Newest First') {
      list.sort(
        (a, b) =>
          (getCreatedAt(b) ? new Date(getCreatedAt(b)).getTime() : 0) -
          (getCreatedAt(a) ? new Date(getCreatedAt(a)).getTime() : 0)
      );
    }

    if (createdFilter === 'Oldest First') {
      list.sort(
        (a, b) =>
          (getCreatedAt(a) ? new Date(getCreatedAt(a)).getTime() : 0) -
          (getCreatedAt(b) ? new Date(getCreatedAt(b)).getTime() : 0)
      );
    }

    return list;
  }, [templates, searchQuery, createdFilter, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredTemplates.length / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginatedTemplates = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredTemplates.slice(start, start + pageSize);
  }, [filteredTemplates, page, pageSize]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
   <Container
     maxWidth="xl"
     sx={{
       py: 3,
       px: { xs: 2, md: 3 },
       background: '#f8fafc',
       minHeight: '100vh',
     }}
   >
      <Box
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: '24px',
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
          border: '1px solid #e2e8f0',
          boxShadow: '0 8px 30px rgba(15,23,42,0.06)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
<Typography variant="h4" sx={{ color: "#1E3A8A", fontWeight: "bold", fontSize: { xs: '1.5rem', md: '2rem' } }}>
         Offer Letter Templates
        </Typography>

            <Typography
              variant="body1"
              sx={{
                color: '#64748b',
              }}
            >
              Create, manage and customize professional offer letter templates.
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{
              borderRadius: '8px',
              px: 3,
              py: 1.1,
              textTransform: 'none',
              fontWeight: 'bold',
              background: '#1E3A8A',
              boxShadow: '0 4px 6px -1px rgba(30, 58, 138, 0.1), 0 2px 4px -1px rgba(30, 58, 138, 0.06)',
              '&:hover': {
                bgcolor: '#1E40AF',
                boxShadow: '0 10px 15px -3px rgba(30, 58, 138, 0.2), 0 4px 6px -2px rgba(30, 58, 138, 0.1)',
              },
            }}
          >
            Create Template
          </Button>
        </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Total Templates */}
        <Grid item xs={12} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2.2,
              borderRadius: '22px',
              border: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              background: '#ffffff',
              color: '#0f172a',
              boxShadow: '0 10px 15px -3px rgba(30, 58, 138, 0.1), 0 4px 6px -2px rgba(30, 58, 138, 0.05)',
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                bgcolor: 'rgba(30, 58, 138, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                color: '#1E3A8A',
                fontSize: 16,
              }}
            >
              Tt
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ color: '#64748b' }}
              >
                Total Templates
              </Typography>

              <Typography
                variant="h5"
                sx={{ fontWeight: 800, lineHeight: 1, color: '#1E3A8A' }}
              >
                {templates.length}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Search */}
        <Grid item xs={12} md={9}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: '18px',
              border: '1px solid #e2e8f0',
              background: '#ffffff',
              boxShadow: '0 6px 20px rgba(15,23,42,0.04)',
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search templates by name, role, keyword..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Paper>
        </Grid>

        {/* Filters */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: '18px',
              border: '1px solid #e2e8f0',
              background: '#ffffff',
              boxShadow: '0 6px 20px rgba(15,23,42,0.04)',
            }}
          >
            <Grid container spacing={1.5} alignItems="center">

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={createdFilter}
                    onChange={(e) => setCreatedFilter(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Created</em>
                    </MenuItem>

                    {CREATED_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    {SORT_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <Select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    {PAGE_SIZES.map((size) => (
                      <MenuItem key={size} value={size}>
                        {size} Per Page
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(_, value) => value && setViewMode(value)}
                    size="small"
                  >
                    <ToggleButton value="grid">
                      <ViewModule fontSize="small" />
                    </ToggleButton>

                    <ToggleButton value="list">
                      <ViewList fontSize="small" />
                    </ToggleButton>
                  </ToggleButtonGroup>

                  <Button
                    startIcon={<Clear />}
                    onClick={() => {
                      setSearchQuery('');
                      setCreatedFilter('');
                      setSortBy('Recently Updated');
                      setPageSize(10);
                    }}
                    variant="outlined"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 'bold',
                      color: '#1E3A8A',
                      borderColor: '#1E3A8A',
                      borderRadius: '8px',
                      '&:hover': {
                        borderColor: '#1E40AF',
                        bgcolor: 'rgba(30, 58, 138, 0.04)',
                      },
                    }}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>

            </Grid>
          </Paper>
        </Grid>
      </Grid>
      </Box>

      {viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {paginatedTemplates.map((template) => {
            const status = getStatus(template);

            return (
              <Grid item xs={12} md={6} key={template.id}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '22px',
                    border: '1px solid #e5e7eb',
                    transition: 'all 0.25s ease',
                    boxShadow: '0 8px 24px rgba(15,23,42,0.06)',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 12px 28px rgba(15,23,42,0.10)',
                    },
                  }}
                >
                <Box
                  sx={{
                    height: '5px',
                    background: 'linear-gradient(90deg, #1E3A8A 0%, #60A5FA 100%)',
                    borderTopLeftRadius: '22px',
                    borderTopRightRadius: '22px',
                  }}
                />
                 <CardContent sx={{ flexGrow: 1, p: 2.2, '&:last-child': { pb: 2.2 } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 2,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              bgcolor: 'rgba(30, 58, 138, 0.1)',
              color: '#1E3A8A',
              width: 42,
              height: 42,
            }}
          >
                          <TextFields fontSize="small" />
                        </Avatar>

                        <Box>
                          <Typography
                            variant="subtitle1"
                           sx={{
                             fontWeight: 800,
                             color: '#0f172a',
                             fontSize: '1.35rem',
                           }}
                          >
                            {template.name}
                          </Typography>

                          <Chip
                            label={status}
                            size="small"
                            {...getChipProps(status)}
                          />
                        </Box>
                      </Box>

                      <Box>
                        <Tooltip title="Preview">
                          <IconButton
                            onClick={() => handlePreview(template)}
                            title="Preview"
                            sx={{ color: '#1E3A8A' }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="More">
                          <IconButton onClick={(e) => openActionsMenu(e, template)}>
                            <MoreVert />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 3,
                        p: 1.75,
                        bgcolor: '#f8fafc',
                        mb: 3,
                        minHeight: 80,
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.7,
                        }}
                      >
                        {stripHtml(template.content) || '(No content)'}
                      </Typography>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Created
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatDate(getCreatedAt(template))}
                        </Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Updated
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatDate(getUpdatedAt(template))}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}

          {paginatedTemplates.length === 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
                <Typography color="text.secondary">
                  No templates found. Create your first template!
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflowX: 'auto' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Template</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTemplates.map((template) => {
                  const status = getStatus(template);

                  return (
                    <TableRow key={template.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            sx={{
                              bgcolor: 'rgba(30, 58, 138, 0.1)',
                              color: '#1E3A8A',
                              width: 36,
                              height: 36,
                            }}
                          >
                            <TextFields fontSize="small" />
                          </Avatar>

                          <Box>
                            <Typography sx={{ fontWeight: 700 }}>{template.name}</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {stripHtml(template.content).slice(0, 90) || 'No content'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={status}
                          size="small"
                          variant="outlined"
                          {...getChipProps(status)}
                        />
                      </TableCell>
                      <TableCell>{formatDate(getCreatedAt(template))}</TableCell>
                      <TableCell>{formatDate(getUpdatedAt(template))}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Preview">
                          <IconButton sx={{ color: '#1E3A8A' }} onClick={() => handlePreview(template)}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="More">
                          <IconButton onClick={(e) => openActionsMenu(e, template)}>
                            <MoreVert />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {paginatedTemplates.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">
                        No templates found. Create your first template!
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Box
        sx={{
          mt: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {(page - 1) * pageSize + 1} to{' '}
          {Math.min(page * pageSize, filteredTemplates.length)} of {filteredTemplates.length}
        </Typography>

        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, value) => setPage(value)}
          color="primary"
          shape="rounded"
        />
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle sx={{ color: '#1E3A8A', fontWeight: 'bold' }}>{editingTemplate ? 'Edit Template' : 'Create New Template'}</DialogTitle>
          <DialogContent dividers>
            <TextField
              name="name"
              label="Template Name"
              fullWidth
              required
              value={formData.name}
              onChange={handleNameChange}
              sx={{ mb: 3 }}
            />

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Letterhead Image (Letter Pad)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button variant="outlined" component="label" disabled={uploading}>
                  {uploading ? <CircularProgress size={24} /> : 'Upload Image'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={uploading}
                  />
                </Button>
                {letterheadPreview && (
                  <Box
                    component="img"
                    src={
                      letterheadPreview.startsWith('http')
                        ? letterheadPreview
                        : `${API_BASE_URL}${letterheadPreview}`
                    }
                    sx={{ height: 50, border: '1px solid #ddd', borderRadius: 1 }}
                  />
                )}
              </Box>
            </Box>

            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Offer Letter Content
              </Typography>
              <Box sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ alignSelf: 'center', mr: 0.5 }}
                >
                  Insert placeholder:
                </Typography>
                {PLACEHOLDERS.map((p) => (
                  <Tooltip key={p.value} title={`Insert ${p.value}`}>
                    <Chip
                      label={p.label}
                      size="small"
                      variant="outlined"
                      onClick={() => insertPlaceholder(p.value)}
                      sx={{ cursor: 'pointer', fontSize: '0.72rem' }}
                    />
                  </Tooltip>
                ))}
                <Tooltip title="Generate content with AI">
                  <Button
                    size="small"
                    startIcon={<StarBorder />}
                    onClick={() => setAiDialogOpen(true)}
                    sx={{
                      ml: 'auto',
                      textTransform: 'none',
                      borderRadius: '12px',
                      background: 'linear-gradient(90deg, #1E3A8A 0%, #1E3A8A 100%)',
                      color: 'white',
                      fontWeight: 600,
                      '&:hover': {
                        opacity: 0.9,
                      },
                    }}
                  >
                    ✨ Generate with AI
                  </Button>
                </Tooltip>
              </Box>
              {formData.content && (
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RestartAlt />}
                    onClick={() => handleImproveAI('regenerate')}
                    disabled={aiLoading}
                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                  >
                    Regenerate
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<History />}
                    onClick={() => handleImproveAI('improve')}
                    disabled={aiLoading}
                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                  >
                    Improve Writing
                  </Button>
                  {aiLoading && <CircularProgress size={20} sx={{ alignSelf: 'center' }} />}
                </Stack>
              )}
              <Box
                sx={{
                  border: '1px solid rgba(0,0,0,0.23)',
                  borderRadius: 1,
                  '& .ql-editor': { minHeight: 280, fontSize: '14px' },
                  '& .ql-toolbar': { borderBottom: '1px solid rgba(0,0,0,0.15)' },
                  '&:focus-within': {
                    borderColor: 'primary.main',
                    boxShadow: '0 0 0 1px #1976d2',
                  },
                }}
              >
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={formData.content}
                  onChange={handleContentChange}
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  placeholder="Write your offer letter content here..."
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, gap: 1 }}>
            <Button 
              onClick={handleCloseDialog}
              sx={{ color: '#64748b', textTransform: 'none', fontWeight: 'bold' }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              sx={{ 
                bgcolor: '#1E3A8A', 
                textTransform: 'none', 
                fontWeight: 'bold',
                borderRadius: '8px',
                px: 3,
                '&:hover': { bgcolor: '#1E40AF' }
              }}
            >
              Save Template
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#1E3A8A', fontWeight: 'bold' }}>Template Preview</DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              width: '100%',
              border: '1px solid #eee',
              boxShadow: '0 0 10px rgba(0,0,0,0.1)',
              backgroundColor: '#fff',
              overflow: 'hidden',
            }}
          >
            {previewData.letterheadUrl && (
              <Box
                component="img"
                src={
                  previewData.letterheadUrl.startsWith('http')
                    ? previewData.letterheadUrl
                    : `${API_BASE_URL}${previewData.letterheadUrl}`
                }
                alt="Company Letterhead"
                sx={{ width: '100%', height: 'auto', display: 'block' }}
              />
            )}
            {isHtmlContent(previewData.content) ? (
              <Box
                sx={{
                  p: 4,
                  color: '#333',
                  fontSize: '0.95rem',
                  lineHeight: 1.8,
                  '& p': { margin: '0 0 10px' },
                  '& h1': { fontSize: '1.6rem', fontWeight: 700, mb: 1 },
                  '& h2': { fontSize: '1.35rem', fontWeight: 700, mb: 1 },
                  '& h3': { fontSize: '1.15rem', fontWeight: 700, mb: 1 },
                  '& ul, & ol': { paddingLeft: '1.5rem', mb: 1 },
                  '& li': { mb: 0.5 },
                  '& strong': { fontWeight: 700 },
                  '& em': { fontStyle: 'italic' },
                  '& u': { textDecoration: 'underline' },
                }}
                dangerouslySetInnerHTML={{ __html: previewData.content }}
              />
            ) : (
              <Box
                sx={{
                  p: 4,
                  whiteSpace: 'pre-wrap',
                  color: '#333',
                  fontSize: '0.95rem',
                  lineHeight: 1.8,
                }}
              >
                {previewData.content}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setPreviewOpen(false)}
            sx={{ color: '#1E3A8A', textTransform: 'none', fontWeight: 'bold' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={actionsAnchorEl}
        open={Boolean(actionsAnchorEl)}
        onClose={closeActionsMenu}
      >
        <MenuItem
          onClick={() => {
            if (actionsTemplate) handleDuplicate(actionsTemplate);
            closeActionsMenu();
          }}
        >
          <ContentCopy fontSize="small" sx={{ mr: 1 }} />
          Duplicate Template
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (actionsTemplate) handlePreview(actionsTemplate);
            closeActionsMenu();
          }}
        >
          <Visibility fontSize="small" sx={{ mr: 1 }} />
          Preview
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionsTemplate) handleOpenDialog(actionsTemplate);
            closeActionsMenu();
          }}
        >
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionsTemplate) handleDelete(actionsTemplate.id);
            closeActionsMenu();
          }}
          sx={{ color: 'error.main' }}
        >
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* AI Generation Dialog */}
      <Dialog open={aiDialogOpen} onClose={() => setAiDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#1E3A8A', fontWeight: 'bold' }}>✨ Generate with AI</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Fill in the details below and AI will generate a professional offer letter for you.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Candidate Name"
                fullWidth
                value={aiData.candidate_name}
                onChange={(e) => setAiData({ ...aiData, candidate_name: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Job Title"
                fullWidth
                value={aiData.job_title}
                onChange={(e) => setAiData({ ...aiData, job_title: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Company Name"
                fullWidth
                value={aiData.company_name}
                onChange={(e) => setAiData({ ...aiData, company_name: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Salary"
                fullWidth
                value={aiData.salary}
                onChange={(e) => setAiData({ ...aiData, salary: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Joining Date"
                fullWidth
                value={aiData.joining_date}
                onChange={(e) => setAiData({ ...aiData, joining_date: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Additional Info (Benefits, Department, etc.)"
                fullWidth
                multiline
                rows={3}
                value={aiData.additional_info}
                onChange={(e) => setAiData({ ...aiData, additional_info: e.target.value })}
                placeholder="e.g. Health insurance, Paid leave, Engineering department, reporting to CTO"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setAiDialogOpen(false)} sx={{ color: '#64748b' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateAI}
            disabled={aiLoading}
            sx={{
              bgcolor: '#1E3A8A',
              textTransform: 'none',
              fontWeight: 'bold',
              minWidth: 120,
              '&:hover': { bgcolor: '#1E40AF' },
            }}
          >
            {aiLoading ? <CircularProgress size={24} color="inherit" /> : 'Generate Offer Letter'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OfferLetterTemplates;
